import React, { useState, useEffect } from 'react';
import {
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import OnboardingStepWrapper from '../OnboardingStepWrapper';
import { useOnboarding } from '../../../context/OnboardingContext';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import interests from '../options/interests.json';
import skills from '../options/skills.json';
import { MultipleChoice } from '@orbit/ui';

interface UserBioFormProps {
  showGlow?: boolean;
}

const steps = ['Onboarding Style', 'Who are you?', 'Your Interests', 'Your Background'];

const UserBioForm: React.FC<UserBioFormProps> = () => {
  const { setData, setStep } = useOnboarding();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Different limits for simple vs advanced mode
  const SIMPLE_LIMIT = 5;
  const ADVANCED_LIMIT = 10;
  const SKILL_LIMIT = 30;

  const [formData, setFormData] = useState({
    complexity: 'simple' as 'simple' | 'advanced',
    background: '',
    businessType: '',
    timeAvailable: '',
    comfortableLearning: true,
    hasCofounders: true,
    spaces: [] as string[],
    spacesSpecific: [] as string[],
    areas: [] as string[],
    areasSpecific: [] as string[],
  });
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Load form data from localStorage on component mount
  useEffect(() => {
    const loadFormDataFromStorage = () => {
      try {
        const stored = localStorage.getItem('onboardingState');
        if (!stored) return;

        const parsedData = JSON.parse(stored);
        if (!parsedData?.data?.userBio) return;

        const userBio = parsedData.data.userBio;

        // Safely extract values with proper type checking and fallbacks
        const complexity = userBio.complexity === 'advanced' ? 'advanced' : 'simple';
        const background = typeof userBio.background === 'string' ? userBio.background : '';
        const businessType = typeof userBio.businessType === 'string' ? userBio.businessType : '';
        const timeAvailable =
          typeof userBio.timeAvailable === 'string' ? userBio.timeAvailable : '';
        const comfortableLearning =
          typeof userBio.comfortableLearning === 'boolean' ? userBio.comfortableLearning : true;
        const hasCofounders =
          typeof userBio.hasCofounders === 'boolean' ? userBio.hasCofounders : true;

        // Safely handle arrays
        const spaces = Array.isArray(userBio.spaces)
          ? userBio.spaces.filter((item: any) => typeof item === 'string')
          : [];
        const spacesSpecific = Array.isArray(userBio.spacesSpecific)
          ? userBio.spacesSpecific.filter((item: any) => typeof item === 'string')
          : [];
        const areas = Array.isArray(userBio.areas)
          ? userBio.areas.filter((item: any) => typeof item === 'string')
          : [];
        const areasSpecific = Array.isArray(userBio.areasSpecific)
          ? userBio.areasSpecific.filter((item: any) => typeof item === 'string')
          : [];

        // Apply stored values to form data
        setFormData((prev) => ({
          ...prev,
          complexity,
          background,
          businessType,
          timeAvailable,
          comfortableLearning,
          hasCofounders,
          spaces,
          spacesSpecific,
          areas,
          areasSpecific,
        }));

        // Determine the appropriate step based on completed data
        let targetStep = 0;

        // Step 1: Basic info completed
        if (background && businessType && timeAvailable) {
          targetStep = 1;
        }

        // Step 2: Spaces selection completed (for simple mode) or spaces + spacesSpecific (for advanced mode)
        if (targetStep === 1 && spaces.length > 0) {
          if (complexity === 'simple' || (complexity === 'advanced' && spacesSpecific.length > 0)) {
            targetStep = 2;
          }
        }

        // Step 3: Areas selection completed (for simple mode) or areas + areasSpecific (for advanced mode)
        if (targetStep === 2 && areas.length > 0) {
          if (complexity === 'simple' || (complexity === 'advanced' && areasSpecific.length > 0)) {
            targetStep = 3;
          }
        }

        setActiveStep(targetStep);
      } catch (error) {
        console.error('Error loading form data from localStorage:', error);
        // Don't throw - just log the error and continue with default form state
      }
    };

    loadFormDataFromStorage();
  }, []);

  // Save form data to localStorage whenever formData changes
  useEffect(() => {
    const saveFormDataToStorage = () => {
      try {
        // Get existing onboarding state
        const existing = localStorage.getItem('onboardingState');
        const existingData = existing ? JSON.parse(existing) : { data: {} };

        // Update with current form data
        existingData.data.userBio = {
          ...formData,
          step: 'user-bio-form',
        };

        localStorage.setItem('onboardingState', JSON.stringify(existingData));
      } catch (error) {
        console.error('Error saving form data to localStorage:', error);
      }
    };

    // Only save if we have some meaningful data (not just initial state)
    if (
      formData.complexity ||
      formData.background ||
      formData.businessType ||
      formData.timeAvailable ||
      formData.spaces.length > 0 ||
      formData.areas.length > 0
    ) {
      saveFormDataToStorage();
    }
  }, [formData]);

  const handleComplexityChange = (complexity: 'simple' | 'advanced') => {
    setFormData((prev) => ({ ...prev, complexity }));
  };

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSwitchChange =
    (field: 'comfortableLearning' | 'hasCofounders') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: event.target.checked }));
    };

  const handleSelectionLimit = (newValue: any[], fieldName: string, limit: number) => {
    if (newValue.length > limit) {
      const limitedValue = newValue.slice(0, limit);
      setSnackbarMessage(
        `You've reached the maximum number of selections for this field (${limit}). Remove some items to add new ones.`,
      );
      setSnackbarOpen(true);
      return limitedValue;
    }
    return newValue;
  };

  const handleContinue = () => {
    const formPayload = {
      userBio: {
        ...formData,
        step: '2b',
      },
    };

    setData(formPayload);
    // Go to business ideas generation step
    setStep('business-ideas-selection');
  };

  // Validation for step 1 (user info)
  const isStep1Valid =
    formData.background &&
    formData.businessType &&
    formData.timeAvailable &&
    (formData.complexity === 'simple' || typeof formData.hasCofounders === 'boolean');

  // Validation for step 2 (spaces selection)
  const isStep2Valid =
    formData.spaces.length > 0 &&
    (formData.complexity === 'simple' || formData.spacesSpecific.length > 0);

  // Validation for step 3 (areas selection)
  const isStep3Valid =
    formData.areas.length > 0 &&
    (formData.complexity === 'simple' || formData.areasSpecific.length > 0);

  const selectSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: '1px',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    backgroundColor: '#262638',
    '& .MuiInputBase-input': {
      padding: '16.5px 14px',
    },
  };

  // Transform grouped options for Autocomplete
  const groupedAreasOptions = Object.entries(skills)
    .flatMap(([area, specifics]) => (specifics as string[]).map((specific) => ({ area, specific })))
    .sort((a, b) => a.area.localeCompare(b.area));
  const filteredGroupedAreasOptions =
    formData.areas.length > 0
      ? groupedAreasOptions.filter((opt) => formData.areas.includes(opt.area))
      : [];

  // Transform grouped options for spaces
  const groupedSpacesOptions = Object.entries(interests)
    .flatMap(([group, specifics]) =>
      (specifics as string[]).map((specific) => ({ group, specific })),
    )
    .sort((a, b) => a.group.localeCompare(b.group));
  const filteredGroupedSpacesOptions =
    formData.spaces.length > 0
      ? groupedSpacesOptions.filter((opt) => formData.spaces.includes(opt.group))
      : [];

  // Extract main options from files
  const spacesMainOptions = Object.keys(interests);
  const areasMainOptions = Object.keys(skills);

  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  return (
    <OnboardingStepWrapper
      title="Ready For Takeoff..."
      subtitle="Share a little about yourself so I can help you come up with relevant business ideas!"
      showBackButton={true}
      headerPosition="top"
      progressStep={activeStep}
      onProgressBack={handleBack}
    >
      <Box className="w-full max-w-4xl space-y-10">
        {!isMobile && (
          <>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {/* Extra spacing between stepper and content */}
            <div className="h-16"></div>
          </>
        )}

        {/* Step content */}
        {activeStep === 0 && (
          <Box>
            {/* Step 1: Simple/Advanced selection */}
            <div className="text-center mb-6 text-lg text-blue-100 font-heading">
              How much should I know before generating your business ideas? More input = more
              relevant and creative ideas tailored to your goals, skills, and interests
            </div>
            <MultipleChoice
              options={[
                {
                  id: 'simple',
                  title: 'Quick Launch',
                  subtitle:
                    'Takes 1-2 minutes. Ideal if you want to see ideas fast and refine them later',
                },
                {
                  id: 'advanced',
                  title: 'Preflight Check',
                  subtitle:
                    "Takes 3-5 minutes. I'll ask about your interests, background, and goals first",
                },
              ]}
              onSelect={(option) => {
                handleComplexityChange(option.id as 'simple' | 'advanced');
                setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
              }}
              showGlow={true}
            />
          </Box>
        )}
        {activeStep === 1 && (
          <Box>
            {/* Step 2: User info */}
            <Box className="flex flex-col md:flex-row gap-6">
              <Box className="flex-1">
                <FormControl fullWidth>
                  <InputLabel shrink>I am a...</InputLabel>
                  <Select
                    label="I am a..."
                    value={formData.background}
                    onChange={(e) => handleFieldChange('background', e.target.value)}
                    className="text-white"
                    sx={selectSx}
                    notched
                  >
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="employee">Employee</MenuItem>
                    <MenuItem value="entrepreneur">Entrepreneur</MenuItem>
                    <MenuItem value="freelancer">Freelancer</MenuItem>
                    <MenuItem value="stay-at-home">Stay-at-Home Parent</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                    <MenuItem value="career-changer">Career Changer</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box className="flex-1">
                <FormControl fullWidth>
                  <InputLabel shrink>I want to build a ___ business</InputLabel>
                  <Select
                    label="I want to build a ___ business"
                    value={formData.businessType}
                    onChange={(e) => handleFieldChange('businessType', e.target.value)}
                    className="text-white"
                    sx={selectSx}
                    notched
                  >
                    <MenuItem value="full-time">Full-time (quit my job)</MenuItem>
                    <MenuItem value="side-hustle">Side hustle (passive income)</MenuItem>
                    <MenuItem value="lifestyle-business">
                      Lifestyle (get paid to do my hobbies)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box className="flex-1">
                <FormControl fullWidth>
                  <InputLabel shrink>I have __ available each week</InputLabel>
                  <Select
                    label="I have __ available each week"
                    value={formData.timeAvailable}
                    onChange={(e) => handleFieldChange('timeAvailable', e.target.value)}
                    className="text-white"
                    sx={selectSx}
                    notched
                  >
                    <MenuItem value="<10">{`<10 hours`}</MenuItem>
                    <MenuItem value="10-20">10-20 hours</MenuItem>
                    <MenuItem value="20-40">20-40 hours</MenuItem>
                    <MenuItem value="40+">40+ hours</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Switches - vertical layout */}
            <Box className="flex flex-col gap-6 pt-4 items-center">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.comfortableLearning}
                    onChange={handleSwitchChange('comfortableLearning')}
                    color="primary"
                  />
                }
                label={
                  <span className="text-white text-lg">I am comfortable learning new skills</span>
                }
                labelPlacement="end"
                className="mx-0"
              />
              {formData.complexity === 'advanced' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasCofounders}
                      onChange={handleSwitchChange('hasCofounders')}
                      color="primary"
                    />
                  }
                  label={
                    <span className="text-white text-lg">I will have one or more cofounders</span>
                  }
                  labelPlacement="end"
                  className="mx-0"
                />
              )}
            </Box>

            {/* Continue button */}
            <Box className="flex justify-center pt-8">
              <Button
                variant="contained"
                size="large"
                onClick={() => setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))}
                disabled={!isStep1Valid}
                sx={{
                  backgroundColor: '#2489f9',
                  '&:hover': {
                    backgroundColor: '#1a6fd9',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: 'rgba(255, 255, 255, 0.38)',
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )}
        {activeStep === 2 && (
          <Box>
            {/* Step 3: Spaces selection */}
            <div className="text-center mb-6 text-lg text-blue-100 font-heading">
              Tell me about your interests and what you're passionate about
            </div>
            <Box className="flex flex-col md:flex-row gap-6">
              {/* First row of autocompletes */}
              <Box className="flex-1">
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  limitTags={isMobile ? 1 : 2}
                  options={spacesMainOptions}
                  value={formData.spaces}
                  onChange={(_, value: string[]) => {
                    // Limit the number of selections
                    const limitedValue = handleSelectionLimit(value, 'spaces', SIMPLE_LIMIT);
                    setFormData((prev) => ({ ...prev, spaces: limitedValue }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Spaces I am interested in..."
                      variant="outlined"
                    />
                  )}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            altAxis: true,
                            tether: false,
                          },
                        },
                      ],
                    },
                  }}
                  sx={{
                    // Mobile optimizations
                    '& .MuiAutocomplete-tag': {
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '4px 8px' : '6px 12px',
                      margin: isMobile ? '2px' : '4px',
                    },
                    '& .MuiOutlinedInput-root': {
                      minHeight: isMobile ? '48px' : '42px', // Larger touch target on mobile
                    },
                  }}
                />
              </Box>
              {formData.complexity === 'advanced' && (
                <Box className="flex-1">
                  {formData.spaces.length === 0 ? (
                    <Tooltip
                      title="Select at least one option in the first field to enable this field."
                      arrow
                    >
                      <Autocomplete
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        limitTags={2}
                        disabled={formData.spaces.length === 0}
                        options={filteredGroupedSpacesOptions}
                        value={groupedSpacesOptions.filter((opt) =>
                          formData.spacesSpecific.includes(opt.specific),
                        )}
                        onChange={(_, value: (string | { group: string; specific: string })[]) => {
                          // Limit the number of selections
                          const limitedValue = handleSelectionLimit(
                            value.map((v) => (typeof v === 'string' ? v : v.specific)),
                            'spacesSpecific',
                            ADVANCED_LIMIT,
                          );
                          setFormData((prev) => ({
                            ...prev,
                            spacesSpecific: limitedValue,
                          }));
                        }}
                        groupBy={(option) => (typeof option === 'string' ? '' : option.group)}
                        getOptionLabel={(option: string | { group: string; specific: string }) =>
                          typeof option === 'string' ? option : option.specific
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="More specifically, I am interested in..."
                            variant="outlined"
                          />
                        )}
                        slotProps={{
                          popper: {
                            placement: 'bottom-start',
                            modifiers: [
                              {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                  altAxis: true,
                                  tether: false,
                                },
                              },
                            ],
                          },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Autocomplete
                      multiple
                      freeSolo
                      disableCloseOnSelect
                      limitTags={2}
                      disabled={formData.spaces.length === 0}
                      options={filteredGroupedSpacesOptions}
                      value={formData.spacesSpecific.map((specific) => {
                        // Find if this specific value exists in the grouped options
                        const existingOption = groupedSpacesOptions.find(
                          (opt) => opt.specific === specific,
                        );
                        if (existingOption) {
                          return existingOption;
                        }
                        // If it's a custom value, return it as a string
                        return specific;
                      })}
                      onChange={(_, value: (string | { group: string; specific: string })[]) => {
                        // Limit the number of selections
                        const limitedValue = handleSelectionLimit(
                          value.map((v) => (typeof v === 'string' ? v : v.specific)),
                          'spacesSpecific',
                          ADVANCED_LIMIT,
                        );
                        setFormData((prev) => ({
                          ...prev,
                          spacesSpecific: limitedValue,
                        }));
                      }}
                      groupBy={(option) => (typeof option === 'string' ? '' : option.group)}
                      getOptionLabel={(option: string | { group: string; specific: string }) =>
                        typeof option === 'string' ? option : option.specific
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="More specifically, I am interested in..."
                          variant="outlined"
                        />
                      )}
                      slotProps={{
                        popper: {
                          placement: 'bottom-start',
                          modifiers: [
                            {
                              name: 'preventOverflow',
                              enabled: true,
                              options: {
                                altAxis: true,
                                tether: false,
                              },
                            },
                          ],
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <div className="text-center mb-6 pt-4 text-sm text-text-secondary font-medium">
              Select from the list and/or type your own!
            </div>

            {/* Continue button for step 2 */}
            <Box className="flex justify-center pt-8">
              <Button
                variant="contained"
                size="large"
                onClick={() => setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))}
                disabled={!isStep2Valid}
                sx={{
                  backgroundColor: '#2489f9',
                  '&:hover': {
                    backgroundColor: '#1a6fd9',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: 'rgba(255, 255, 255, 0.38)',
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )}
        {activeStep === 3 && (
          <Box>
            {/* Step 4: Areas selection */}
            <div className="text-center mb-6 text-lg text-blue-100 font-heading">
              Tell me about your experience and what you're good at
            </div>
            <Box className="flex flex-col md:flex-row gap-6">
              {/* Second row of autocompletes */}
              <Box className="flex-1">
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  limitTags={2}
                  options={areasMainOptions}
                  value={formData.areas}
                  onChange={(_, value: string[]) => {
                    // Limit the number of selections
                    const limitedValue = handleSelectionLimit(value, 'areas', SIMPLE_LIMIT);
                    setFormData((prev) => ({ ...prev, areas: limitedValue }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Areas I am familiar with..." variant="outlined" />
                  )}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          enabled: true,
                          options: {
                            altAxis: true,
                            tether: false,
                          },
                        },
                      ],
                    },
                  }}
                />
              </Box>
              {formData.complexity === 'advanced' && (
                <Box className="flex-1">
                  {formData.areas.length === 0 ? (
                    <Tooltip
                      title="Select at least one option in the first field to enable this field."
                      arrow
                    >
                      <Autocomplete
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        limitTags={2}
                        disabled={formData.areas.length === 0}
                        options={filteredGroupedAreasOptions}
                        value={formData.areasSpecific.map((specific) => {
                          // Find if this specific value exists in the grouped options
                          const existingOption = groupedAreasOptions.find(
                            (opt) => opt.specific === specific,
                          );
                          if (existingOption) {
                            return existingOption;
                          }
                          // If it's a custom value, return it as a string
                          return specific;
                        })}
                        onChange={(_, value: (string | { area: string; specific: string })[]) => {
                          // Limit the number of selections
                          const limitedValue = handleSelectionLimit(
                            value.map((v) => (typeof v === 'string' ? v : v.specific)),
                            'areasSpecific',
                            SKILL_LIMIT,
                          );
                          setFormData((prev) => ({
                            ...prev,
                            areasSpecific: limitedValue,
                          }));
                        }}
                        groupBy={(option) => (typeof option === 'string' ? '' : option.area)}
                        getOptionLabel={(option) =>
                          typeof option === 'string' ? option : option.specific
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="More specifically, I have skills in..."
                            variant="outlined"
                          />
                        )}
                        slotProps={{
                          popper: {
                            placement: 'bottom-start',
                            modifiers: [
                              {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                  altAxis: true,
                                  tether: false,
                                },
                              },
                            ],
                          },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Autocomplete
                      multiple
                      freeSolo
                      disableCloseOnSelect
                      limitTags={2}
                      disabled={formData.areas.length === 0}
                      options={filteredGroupedAreasOptions}
                      value={formData.areasSpecific.map((specific) => {
                        // Find if this specific value exists in the grouped options
                        const existingOption = groupedAreasOptions.find(
                          (opt) => opt.specific === specific,
                        );
                        if (existingOption) {
                          return existingOption;
                        }
                        // If it's a custom value, return it as a string
                        return specific;
                      })}
                      onChange={(_, value: (string | { area: string; specific: string })[]) => {
                        // Limit the number of selections
                        const limitedValue = handleSelectionLimit(
                          value.map((v) => (typeof v === 'string' ? v : v.specific)),
                          'areasSpecific',
                          SKILL_LIMIT,
                        );
                        setFormData((prev) => ({
                          ...prev,
                          areasSpecific: limitedValue,
                        }));
                      }}
                      groupBy={(option) => (typeof option === 'string' ? '' : option.area)}
                      getOptionLabel={(option) =>
                        typeof option === 'string' ? option : option.specific
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="More specifically, I have skills in..."
                          variant="outlined"
                        />
                      )}
                      slotProps={{
                        popper: {
                          placement: 'bottom-start',
                          modifiers: [
                            {
                              name: 'preventOverflow',
                              enabled: true,
                              options: {
                                altAxis: true,
                                tether: false,
                              },
                            },
                          ],
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <div className="text-center mb-6 pt-4 text-sm text-text-secondary font-medium">
              Select from the list and/or type your own!
            </div>

            {/* Continue button for step 3 */}
            <Box className="flex justify-center pt-8">
              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                disabled={!isStep3Valid}
                sx={{
                  backgroundColor: '#2489f9',
                  '&:hover': {
                    backgroundColor: '#1a6fd9',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    color: 'rgba(255, 255, 255, 0.38)',
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </OnboardingStepWrapper>
  );
};

export default UserBioForm;
